import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import FeeWarning from "../components/FeeWarning";
import PendingFeePopup from "../components/PendingFeePopup";
import EnrolledSubsection from "../components/EnrolledSubsection";
import AttendanceQrDialog from "../components/AttendanceQrDialog";
import AnimateComponent from "../components/AnimateComponent";
import NotificationContext from "../context/NotificationContext";
import AccessDenied from "../components/AccessDenied";
import Loader from "../components/Loader";
import { useViewPermission } from "../context/PermissionContext";
import {
  getFeeDueMessaging,
  getFeeWarningSchedule,
} from "../utils/dateFormat";
import { resolveStudentFeeState } from "../utils/studentFeeState";
import PaymentHistoryView from "../components/PaymentHistoryView";
import * as registrationsApi from "../api/registrations";
import "./MyCourses.css";

const LMS_URL = "https://zainematics.learn360lms.com/";

function paymentChipClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "paid") return "mycourses-chip mycourses-chip--paid";
  if (normalized === "unpaid") return "mycourses-chip mycourses-chip--unpaid";
  if (normalized === "partially_paid" || normalized === "pending") {
    return "mycourses-chip mycourses-chip--pending";
  }
  return "mycourses-chip mycourses-chip--other";
}

function normalizeCourseStatus(status) {
  return String(status || "").toLowerCase().trim();
}

function isDroppedStatus(status) {
  return normalizeCourseStatus(status).startsWith("dropped");
}

function partitionCoursesByStatus(courses = []) {
  const enrolled = [];
  const pending = [];
  const unenrolled = [];
  const closed = [];
  const other = [];

  for (const course of courses) {
    const status = normalizeCourseStatus(course.status);
    if (status === "enrolled") enrolled.push(course);
    else if (status === "pending") pending.push(course);
    else if (status === "unenrolled") unenrolled.push(course);
    else if (status === "transferred" || isDroppedStatus(status)) {
      closed.push(course);
    } else other.push(course);
  }

  return { enrolled, pending, unenrolled, closed, other };
}

function CourseStatusSection({
  id,
  label,
  hint,
  courses,
  emptyText,
  registrationId,
  onDrop,
  onOpenLms,
  onOpenAttendanceQr,
  feeRestrictedIds,
  droppingKey,
  alwaysShow = true,
}) {
  if (!alwaysShow && courses.length === 0) return null;

  return (
    <div className="mycourses-subsection" id={id}>
      <div className="mycourses-subsection__head">
        <p className="mycourses-subsection__label">{label}</p>
        <span className="mycourses-subsection__count">{courses.length}</span>
      </div>
      {hint ? <p className="mycourses-subsection__hint">{hint}</p> : null}
      {courses.length === 0 ? (
        <p className="mycourses-subsection-empty">{emptyText}</p>
      ) : (
        <div className="mycourses-course-grid">
          {courses.map((course) => {
            const dropKey = `${registrationId}-${course.id}`;
            const feeRestricted = feeRestrictedIds?.has(String(course.id));
            return (
              <EnrolledSubsection
                key={`${course.id}-${course.status}`}
                course={course}
                onDrop={onDrop}
                onOpenLms={onOpenLms}
                onOpenAttendanceQr={onOpenAttendanceQr}
                feeRestricted={feeRestricted}
                dropping={droppingKey === dropKey}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MyCourses() {
  const { user, isAuthenticated } = useAuth0();

  const [courses, setcourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [droppingKey, setDroppingKey] = useState(null);
  const [checkoutReg, setCheckoutReg] = useState(null);
  const [lmsFeeGate, setLmsFeeGate] = useState(null);
  const [historyRegId, setHistoryRegId] = useState(null);
  const [attendanceQr, setAttendanceQr] = useState(null);
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_my_courses");

  const historyReg = useMemo(
    () =>
      courses.find(
        (reg) => String(reg.id || reg.registrationId) === String(historyRegId),
      ) || null,
    [courses, historyRegId],
  );

  const historyFeeState = useMemo(
    () => (historyReg ? resolveStudentFeeState(historyReg) : null),
    [historyReg],
  );

  const fetchMyCourses = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoadingCourses(true);
      setLoadError(null);
    }
    try {
      const response = await registrationsApi.myCourses();
      setcourses(response.data || []);
      if (silent) setLoadError(null);
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message,
      );
      if (!silent) {
        setLoadError(
          error?.response?.data?.message ||
            "We could not load your courses. Please try again.",
        );
      }
    } finally {
      if (!silent) setLoadingCourses(false);
    }
  };

  useEffect(() => {
    if (permLoading || !hasPermission || !user?.email) return;

    let cancelled = false;
    (async () => {
      setLoadingCourses(true);
      setLoadError(null);
      try {
        const response = await registrationsApi.myCourses();
        if (!cancelled) setcourses(response.data || []);
      } catch (error) {
        console.error(
          "Error:",
          error.response ? error.response.data : error.message,
        );
        if (!cancelled) {
          setLoadError(
            error?.response?.data?.message ||
              "We could not load your courses. Please try again.",
          );
        }
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email, hasPermission, permLoading]);

  const handleDrop = (registration, course) => {
    const registrationId = registration.id || registration.registrationId;
    notificationCtx.show({
      message: `Drop "${course.title}" from your registration?`,
      onConfirm: async () => {
        const dropKey = `${registrationId}-${course.id}`;
        setDroppingKey(dropKey);
        try {
          await registrationsApi.dropByStudent(registrationId, {
            courseId: course.id,
          });
          notificationCtx.show({
            message: "Course dropped successfully",
            stat: "success",
          });
          setcourses((prev) =>
            prev.map((reg) => {
              if ((reg.id || reg.registrationId) !== registrationId) return reg;
              return {
                ...reg,
                courses: (reg.courses || []).map((item) =>
                  item.id === course.id
                    ? { ...item, status: "dropped" }
                    : item,
                ),
              };
            }),
          );
          await fetchMyCourses({ silent: true });
        } catch (err) {
          console.error(err);
          notificationCtx.show({
            message:
              err?.response?.data?.message ||
              "An error occurred while dropping the course",
            stat: "error",
          });
        } finally {
          setDroppingKey(null);
        }
      },
    });
  };

  // Generic fee popup on load (same as Home) — dismissible, not timer-gated
  useEffect(() => {
    if (loadingCourses) return;
    const owed = courses.find((reg) => resolveStudentFeeState(reg).needsPayment);
    setCheckoutReg(owed || null);
  }, [courses, loadingCourses]);

  const openLmsWindow = () => {
    window.open(LMS_URL, "_blank", "noopener,noreferrer");
  };

  const handleOpenLms = (registration, course) => {
    const feeState = resolveStudentFeeState(registration);
    const charged = (feeState.feeCourses || []).find(
      (item) => String(item.id) === String(course.id),
    );

    if (!feeState.needsPayment || !charged) {
      openLmsWindow();
      return;
    }

    const schedule = getFeeWarningSchedule(registration.duedate);
    setLmsFeeGate({
      courses: [charged],
      // Prefer this course's net feeOwed; fall back to registration payable.
      amount:
        charged.chargedRemaining != null
          ? charged.chargedRemaining
          : Number(feeState.outstanding) || 0,
      durationSec: schedule.durationSec,
      message: schedule.message,
    });
  };

  const closeLmsFeeGate = () => {
    setLmsFeeGate(null);
    openLmsWindow();
  };

  if (permLoading) {
    return (
      <>
        <Navbar />
        <div className="mycourses-page mycourses-page--loading">
          <div className="mycourses-page__inner">
            <div className="mycourses-loading" aria-live="polite" aria-busy="true">
              <Loader />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  const showingHistory = Boolean(historyFeeState);

  return (
    <>
      <Navbar />
      {checkoutReg && !showingHistory ? (
        <PendingFeePopup
          regObj={checkoutReg}
          onClose={() => setCheckoutReg(null)}
          onResolved={() => {
            setCheckoutReg(null);
            fetchMyCourses({ silent: true });
          }}
        />
      ) : null}
      {lmsFeeGate ? (
        <FeeWarning
          time={lmsFeeGate.durationSec}
          closeFeeWarning={closeLmsFeeGate}
          onDismiss={() => setLmsFeeGate(null)}
          feeMissingCourses={lmsFeeGate.courses}
          feeMissingAmount={lmsFeeGate.amount}
          feewarningmsg={lmsFeeGate.message}
          title="LMS access restricted"
          intro="Fee is outstanding for this course. Pay to keep access clear:"
          continueLabel="Continue to LMS"
        />
      ) : null}
      {attendanceQr ? (
        <AttendanceQrDialog
          courseTitle={attendanceQr.courseTitle}
          attendanceToken={attendanceQr.attendanceToken}
          registrationId={attendanceQr.registrationId}
          onClose={() => setAttendanceQr(null)}
        />
      ) : null}
      <div
        className={`mycourses-page${
          loadingCourses ? " mycourses-page--loading" : ""
        }`}
      >
        <div className="mycourses-page__inner">
          {showingHistory ? (
            <AnimateComponent>
              <PaymentHistoryView
                groups={historyFeeState.historyGroups}
                totals={historyFeeState.totals}
                onBack={() => setHistoryRegId(null)}
              />
            </AnimateComponent>
          ) : null}

          {!showingHistory && !loadingCourses ? (
            <AnimateComponent>
              <header className="mycourses-header">
                <p className="mycourses-header__eyebrow">Your enrollment</p>
                <h1 className="mycourses-header__title">My Courses</h1>
                <p className="mycourses-header__lede">
                  Review your registration details, payment status, and enrolled
                  classes in one place.
                </p>
              </header>
            </AnimateComponent>
          ) : null}

          {showingHistory ? null : loadingCourses ? (
            <div className="mycourses-loading" aria-live="polite" aria-busy="true">
              <Loader />
            </div>
          ) : loadError ? (
            <div className="mycourses-error" role="alert">
              <h2 className="mycourses-error__title">Something went wrong</h2>
              <p className="mycourses-error__copy">{loadError}</p>
              <button
                type="button"
                className="mycourses-error__cta"
                onClick={fetchMyCourses}
              >
                Try again
              </button>
            </div>
          ) : courses.length === 0 ? (
            <div className="mycourses-empty">
              <h2 className="mycourses-empty__title">No courses yet</h2>
              <p className="mycourses-empty__copy">
                You are not registered for any courses right now. Explore the
                catalog and register when you are ready.
              </p>
              <Link to="/registercourse" className="mycourses-empty__cta">
                Browse courses
              </Link>
              <Link to="/" className="mycourses-empty__link">
                Back to home
              </Link>
            </div>
          ) : (
            <div className="mycourses-stack">
              {courses.map((obj, index) => {
                const registrationId = obj.id || obj.registrationId;
                const byStatus = partitionCoursesByStatus(obj.courses);
                const feeState = resolveStudentFeeState(obj);
                const needsPayment = feeState.needsPayment;
                const feeRestrictedIds = new Set(
                  (feeState.feeCourses || []).map((course) => String(course.id)),
                );
                const dueMessaging = getFeeDueMessaging(obj.duedate);
                const showFeeActions =
                  needsPayment || Boolean(feeState.historyGroups?.length);
                const profileLine = [user?.name, obj.city]
                  .filter(Boolean)
                  .join(" · ");
                const modeLabel = obj.feemode
                  ? `${String(obj.feemode).charAt(0).toUpperCase()}${String(
                      obj.feemode,
                    ).slice(1)} payment`
                  : null;

                return (
                  <AnimateComponent
                    key={registrationId}
                    delay={0.08 + index * 0.04}
                  >
                    <div className="mycourses-stack">
                      <section
                        className="mycourses-panel mycourses-panel--account"
                        aria-labelledby={`account-details-${registrationId}`}
                      >
                        <div className="mycourses-account">
                          <div className="mycourses-account__main">
                            <div className="mycourses-account__intro">
                              <h2
                                id={`account-details-${registrationId}`}
                                className="mycourses-account__name"
                              >
                                {profileLine || "Your account"}
                              </h2>
                              <div className="mycourses-account__chips">
                                <span
                                  className={paymentChipClass(
                                    feeState.paymentStatus,
                                  )}
                                >
                                  {feeState.paymentStatusLabel}
                                </span>
                                {modeLabel ? (
                                  <span className="mycourses-soft-chip mycourses-soft-chip--lavender">
                                    {modeLabel}
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            <p className="mycourses-account__stats">
                              <span>
                                {byStatus.enrolled.length} enrolled
                              </span>
                              <span aria-hidden="true">·</span>
                              <span>
                                {byStatus.pending.length} pending
                              </span>
                              <span aria-hidden="true">·</span>
                              <span
                                className={
                                  feeState.outstanding > 0
                                    ? "mycourses-account__stat--due"
                                    : undefined
                                }
                              >
                                {feeState.outstanding > 0
                                  ? `${feeState.outstandingLabel} due`
                                  : "No balance due"}
                              </span>
                            </p>

                            {needsPayment ? (
                              <p className="mycourses-account__due">
                                {dueMessaging.pillLabel}
                              </p>
                            ) : null}
                          </div>

                          {showFeeActions ? (
                            <div className="mycourses-fee-actions">
                              {needsPayment ? (
                                <button
                                  type="button"
                                  className={`mycourses-fee-action mycourses-fee-action--pay mycourses-fee-action--${dueMessaging.tone}`}
                                  onClick={() => setCheckoutReg(obj)}
                                >
                                  <span className="mycourses-fee-action__label">
                                    Pay fees
                                  </span>
                                  <span className="mycourses-fee-action__meta">
                                    {feeState.outstandingLabel}
                                  </span>
                                </button>
                              ) : null}
                              {feeState.historyGroups?.length ? (
                                <button
                                  type="button"
                                  className="mycourses-fee-action mycourses-fee-action--history"
                                  onClick={() =>
                                    setHistoryRegId(registrationId)
                                  }
                                >
                                  <span className="mycourses-fee-action__label">
                                    Payment history
                                  </span>
                                  <span className="mycourses-fee-action__meta">
                                    By course
                                  </span>
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </section>

                      <section
                        className="mycourses-panel mycourses-panel--courses"
                        aria-labelledby={`registered-courses-${registrationId}`}
                      >
                        <div className="mycourses-panel__head">
                          <h2
                            id={`registered-courses-${registrationId}`}
                            className="mycourses-panel__title"
                          >
                            Your courses
                          </h2>
                          <span className="mycourses-panel__meta">
                            {obj.courses.length} total
                          </span>
                        </div>

                        <CourseStatusSection
                          id={`enrolled-${registrationId}`}
                          label="Enrolled"
                          hint="Active classes you can join on the LMS. Courses with outstanding fees require a fee notice before opening the LMS."
                          courses={byStatus.enrolled}
                          emptyText="No enrolled courses yet."
                          registrationId={registrationId}
                          feeRestrictedIds={feeRestrictedIds}
                          onOpenLms={(course) => handleOpenLms(obj, course)}
                          onOpenAttendanceQr={(course) =>
                            setAttendanceQr({
                              courseTitle: course.title,
                              attendanceToken: course.attendanceToken,
                              registrationId: obj.id,
                            })
                          }
                        />
                        <CourseStatusSection
                          id={`pending-${registrationId}`}
                          label="Pending"
                          hint="Waiting on fee confirmation. You can drop a course here if needed."
                          courses={byStatus.pending}
                          emptyText="No pending courses."
                          registrationId={registrationId}
                          onDrop={(item) => handleDrop(obj, item)}
                          droppingKey={droppingKey}
                        />
                        <CourseStatusSection
                          id={`unenrolled-${registrationId}`}
                          label="Unenrolled"
                          hint="Courses that are currently inactive on your registration."
                          courses={byStatus.unenrolled}
                          emptyText="No unenrolled courses."
                          registrationId={registrationId}
                        />
                        <CourseStatusSection
                          id={`closed-${registrationId}`}
                          label="Dropped / transferred"
                          hint="Courses removed or moved off this registration. Each card shows its exact status."
                          courses={byStatus.closed}
                          emptyText="No dropped or transferred courses."
                          registrationId={registrationId}
                        />
                        <CourseStatusSection
                          id={`other-${registrationId}`}
                          label="Other"
                          courses={byStatus.other}
                          emptyText="No other courses."
                          registrationId={registrationId}
                          alwaysShow={byStatus.other.length > 0}
                        />
                      </section>
                    </div>
                  </AnimateComponent>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
